import React from "react";
import { Navigate, Link } from "react-router-dom";
import { Printer, Phone, MapPin, Globe, MessageCircle, ArrowLeft } from "lucide-react";
import { LOGOS } from "@/lib/brand";
import { useAuth } from "@/contexts/AuthContext";

const CARD_W = "85mm";
const CARD_H = "55mm";

function CardRecto() {
  return (
    <div
      data-testid="card-recto"
      className="card-print relative overflow-hidden bg-black text-white"
      style={{ width: CARD_W, height: CARD_H }}
    >
      {/* Halo radial subtil */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(120% 80% at 100% 0%, rgba(255,255,255,0.06), transparent 60%)",
        }}
      />
      {/* Bandes 3 couleurs en haut */}
      <div className="absolute top-0 left-0 right-0 h-[2mm] flex">
        <div className="flex-1" style={{ backgroundColor: "#8b5cf6" }} />
        <div className="flex-1" style={{ backgroundColor: "#ea580c" }} />
        <div className="flex-1" style={{ backgroundColor: "#0ea5e9" }} />
      </div>

      <div className="relative h-full w-full flex p-[5mm]">
        {/* Colonne gauche : Logo */}
        <div className="flex flex-col justify-center items-center" style={{ width: "32mm" }}>
          <img
            src={LOGOS.groupe}
            alt="Equinoxe Groupe"
            style={{ width: "32mm", height: "38mm", objectFit: "contain" }}
          />
        </div>

        {/* Séparateur vertical */}
        <div className="self-stretch w-[0.2mm] bg-white/15 mx-[3mm]" />

        {/* Colonne droite : Coordonnées */}
        <div className="flex-1 flex flex-col justify-center gap-[2.2mm]">
          <div className="flex items-start gap-[2mm]">
            <Phone size={10} style={{ color: "#fff", marginTop: "0.4mm", flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: "3mm", fontWeight: 600, letterSpacing: "0.02em" }}>
                05 32 18 90 44
              </div>
              <div
                className="flex items-center gap-[1mm]"
                style={{ fontSize: "1.8mm", color: "#9ca3af", marginTop: "0.3mm" }}
              >
                <MessageCircle size={6} style={{ color: "#22c55e" }} />
                <span>Disponible sur WhatsApp</span>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-[2mm]">
            <Globe size={10} style={{ color: "#fff", marginTop: "0.4mm", flexShrink: 0 }} />
            <div style={{ fontSize: "2.6mm", fontWeight: 500 }}>groupeequinoxe.fr</div>
          </div>

          <div className="flex items-start gap-[2mm]">
            <MapPin size={10} style={{ color: "#fff", marginTop: "0.4mm", flexShrink: 0 }} />
            <div style={{ fontSize: "2.3mm", lineHeight: 1.4, color: "#d4d4d4" }}>
              46090 FRANCOULÈS
              <br />
              46250 CAZALS
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CardVerso() {
  const VERSO_LOGO = "https://customer-assets.emergentagent.com/job_enterprise-hub-357/artifacts/6sv95ykm_Equinoxe_Groupe_noir-resize.png";
  return (
    <div
      data-testid="card-verso"
      className="card-print relative overflow-hidden bg-black text-white"
      style={{ width: CARD_W, height: CARD_H }}
    >
      {/* Halo */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(80% 100% at 50% 50%, rgba(255,255,255,0.08), transparent 60%)",
        }}
      />

      {/* Bandes 3 couleurs en bas */}
      <div className="absolute bottom-0 left-0 right-0 h-[2mm] flex">
        <div className="flex-1" style={{ backgroundColor: "#8b5cf6" }} />
        <div className="flex-1" style={{ backgroundColor: "#ea580c" }} />
        <div className="flex-1" style={{ backgroundColor: "#0ea5e9" }} />
      </div>

      <div className="relative h-full w-full flex flex-col items-center justify-center text-center px-[6mm] pb-[10mm]">
        <img
          src={VERSO_LOGO}
          alt="Equinoxe Groupe"
          style={{ width: "50mm", height: "50mm", objectFit: "contain" }}
        />
      </div>

      <div
        className="absolute left-0 right-0 flex items-center justify-center gap-[2mm]"
        style={{ bottom: "5mm", fontSize: "1.8mm" }}
      >
        <span style={{ color: "#8b5cf6", fontWeight: 600 }}>EQUINOXE STUDIO</span>
        <span style={{ color: "#666" }}>·</span>
        <span style={{ color: "#ea580c", fontWeight: 600 }}>EQUINOXE SÉCURITÉ</span>
        <span style={{ color: "#666" }}>·</span>
        <span style={{ color: "#0ea5e9", fontWeight: 600 }}>EQUINOXE PROPRETÉ</span>
      </div>
    </div>
  );
}

export default function BusinessCards() {
  const { user, ready } = useAuth();
  if (ready === false) return null;
  if (ready && !user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="page-business-cards">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-white mb-6 print:hidden"
          data-testid="cards-back-admin"
        >
          <ArrowLeft size={14} /> Retour au tableau de bord
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 print:hidden">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Communication</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
              Cartes de visite — Groupe Equinoxe
            </h1>
            <p className="mt-3 text-zinc-400 text-sm max-w-2xl">
              Format standard 85 × 55 mm, recto-verso. Cliquez sur "Imprimer" pour générer un PDF
              ou prendre une capture d'écran pour votre imprimeur.
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => window.print()}
              data-testid="print-cards"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white text-black font-semibold hover:bg-zinc-200"
            >
              <Printer size={16} /> Imprimer / PDF
            </button>
          </div>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-10 print:gap-[10mm]">
          <div className="flex flex-col items-center">
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4 print:hidden">
              Recto
            </div>
            <div className="card-shadow">
              <CardRecto />
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4 print:hidden">
              Verso
            </div>
            <div className="card-shadow">
              <CardVerso />
            </div>
          </div>
        </div>

        {/* Specs */}
        <div
          className="mt-16 rounded-xl border border-white/10 bg-[#0e0e0e] p-8 print:hidden"
          data-testid="card-specs"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">
            Spécifications pour l'imprimeur
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
            <Spec label="Format" value="85 × 55 mm" />
            <Spec label="Recto-verso" value="Oui" />
            <Spec label="Quadrichromie" value="CMJN" />
            <Spec label="Fond perdu conseillé" value="+3 mm sur chaque côté" />
            <Spec label="Résolution" value="300 dpi minimum" />
            <Spec label="Papier suggéré" value="Mat 350 g/m² ou Soft-touch" />
            <Spec label="Finition suggérée" value="Pelliculage mat + vernis sélectif sur logo" />
            <Spec label="Couleurs accents" value="Violet #8b5cf6 · Orange #ea580c · Bleu #0ea5e9" />
          </div>
        </div>
      </div>

      <style>{`
        .card-shadow {
          box-shadow: 0 24px 60px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04);
          border-radius: 2mm;
          overflow: hidden;
        }
        @media print {
          @page { size: 210mm 297mm; margin: 15mm; }
          html, body { background: white !important; color: black !important; }
          nav, header, footer { display: none !important; }
          .card-shadow { box-shadow: none !important; border-radius: 0; }
          .card-print { box-shadow: none !important; page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className="mt-1 text-white">{value}</div>
    </div>
  );
}
